import { useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
// A valid PayPal REST Client ID is a long token, never an email address.
const PAYPAL_READY = !!PAYPAL_CLIENT_ID && !PAYPAL_CLIENT_ID.includes("@") && PAYPAL_CLIENT_ID.length > 30;

/**
 * PayPalPayButton — renders PayPal Buttons; creates the order server-side and captures server-side.
 * props: purpose, item, name, email, phone?, application_id?, onPaid?, disabled?
 */
export default function PayPalPayButton({ purpose, item, name, email, phone, application_id, onPaid, onBeforePay, disabled }) {
    const [paid, setPaid] = useState(false);

    if (!PAYPAL_READY) return null;

    if (paid) {
        return (
            <span className="flex items-center gap-2 label-mono text-[var(--emerald)]" data-testid="paypal-done">
                <ShieldCheck className="w-4 h-4" /> Payment complete
            </span>
        );
    }

    if (disabled) {
        return (
            <div className="opacity-50 pointer-events-none label-mono text-[var(--ivory)]/50" data-testid="paypal-disabled">
                Enter name & email to pay with PayPal
            </div>
        );
    }

    return (
        <div data-testid="paypal-buttons">
            <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD", intent: "capture" }}>
                <PayPalButtons
                    style={{ layout: "horizontal", color: "gold", shape: "rect", label: "pay", height: 45 }}
                    createOrder={async () => {
                        if (onBeforePay) {
                            try { onBeforePay(); } catch (_e) { /* noop */ }
                        }
                        try {
                            const { data } = await api.post("/payments/paypal/create-order", {
                                purpose, item, name, email, phone, application_id,
                            });
                            return data.order_id;
                        } catch (e) {
                            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
                            throw e;
                        }
                    }}
                    onApprove={async (data) => {
                        try {
                            await api.post("/payments/paypal/capture", { order_id: data.orderID });
                            setPaid(true);
                            toast.success("PayPal payment successful — a receipt is on its way");
                            if (onPaid) onPaid();
                        } catch (e) {
                            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Capture failed");
                        }
                    }}
                    onError={() => toast.error("PayPal encountered an error. Please try again.")}
                />
            </PayPalScriptProvider>
        </div>
    );
}
